import React, { useContext, useEffect, useRef, useState } from "react";
import { Box, Container, Grid, IconButton, LinearProgress, Typography } from "@mui/material";
import bkstApi from "../../api";
import currency from "currency.js";
import { UserContext } from "../../context/UserContext";
import { DataGrid } from "@mui/x-data-grid";
import moment from "moment";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { Link as RouterLink } from "react-router-dom";
import { useParams } from "react-router-dom";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";

const columns = [
  {
    field: "time",
    headerName: "Timestamp",
    type: "number",
    renderCell: (params) => (
      <div>
        <Box py={1}>
          <Typography variant="caption" display="block">
            {moment(params.value).format("ll")}
          </Typography>
          <Typography variant="overline" display="block" color="textSecondary">
            {moment(params.value).format("LT")}
          </Typography>
        </Box>
      </div>
    ),
    width: 120,
  },
  {
    field: "grossAmount",
    headerName: "Gross Amount",
    type: "number",
    valueFormatter: ({ value }) => currency(value, { fromCents: true }).format(),
    width: 120,
  },
  {
    field: "fee",
    headerName: "Fee",
    type: "number",
    valueFormatter: ({ value }) => currency(value, { fromCents: true }).format(),
    width: 120,
  },
  {
    field: "netAmount",
    headerName: "Net Amount",
    type: "number",
    valueFormatter: ({ value }) => currency(value, { fromCents: true }).format(),
    width: 120,
  },
  {
    field: "link",
    headerName: "View",
    renderCell: (params) => (
      <IconButton component={RouterLink} to={params.value} disabled={!params.value}>
        <VisibilityOutlinedIcon />
      </IconButton>
    ),
    width: 100,
  },
];

export default function PayoutDetails(props) {
  const { user } = useContext(UserContext);
  const { payoutId } = useParams();

  const [payout, setPayout] = useState();
  const [loading, setLoading] = useState(true);
  const apiRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    bkstApi.get(`/my/payouts/${payoutId}`).then((res) => {
      setPayout(res.data.payout);
      setLoading(false);
    });
  }, [payoutId]);

  const exportToCSV = () => {
    if (apiRef.current) {
      const payoutDate = moment(payout.summary.created * 1000).format("YYYY-MM-DD");
      apiRef.current.exportDataAsCsv({
        fileName: `Payout_Details_${payoutDate}`,
        fields: ["time", "grossAmount", "fee", "netAmount"],
      });
    }
  };

  if (loading) {
    return <LinearProgress />;
  } else {
    return (
      <Box>
        <Container maxWidth="md">
          <Box>
            <Box my={5}>
              <Typography variant="h1" gutterBottom>
                Payout on {moment(payout.summary.created * 1000).format("ll")}
              </Typography>
              <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                Showing a breakdown of all transactions included in this payout
              </Typography>
            </Box>
          </Box>
          {payout && (
            <Box my={2}>
              <Grid container spacing={2}>
                <Grid item xs={6} align="center">
                  <Box p={2} style={{ backgroundColor: "#ECECEC" }}>
                    <Typography variant="h4">
                      {currency(Math.abs(payout.summary.amount), { fromCents: true }).format()}
                    </Typography>
                    <Typography variant="overline" color="textSecondary">
                      AMOUNT
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={6} align="center">
                  <Box p={2} style={{ backgroundColor: "#ECECEC" }}>
                    <Typography variant="h4">{payout.transactions.length}</Typography>
                    <Typography variant="overline" color="textSecondary">
                      TRANSACTIONS
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          )}
          {payout?.transactions?.length > 0 && (
            <Box sx={{ display: "flex", justifyContent: "flex-end", alignItems: "center" }}>
              <IconButton onClick={exportToCSV}>
                <FileDownloadOutlinedIcon />
              </IconButton>
            </Box>
          )}
          <Box sx={{ width: "100%", display: "grid" }}>
            <DataGrid
              apiRef={apiRef}
              rows={payout.transactions.map((o) => {
                return {
                  id: o.id,
                  time: o.created * 1000,
                  grossAmount: o.grossAmount,
                  fee: o.netFee,
                  netAmount: o.netAmount,
                  description: o.description,
                  type: o.type,
                  link: o.orderId
                    ? `/order/${o.orderId}`
                    : o.reqId
                      ? `/redirect?from=quote&id=${o.reqId}&to=order`
                      : "",
                };
              })}
              columns={columns}
              autoHeight
              getRowHeight={(params) => "auto"}
              disableRowSelectionOnClick
              initialState={{
                sorting: {
                  sortModel: [{ field: "time", sort: "desc" }],
                },
              }}
            />
          </Box>
        </Container>
      </Box>
    );
  }
}
